import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Shovel, LayoutGrid, List as ListIcon, Layers, Grid2x2 } from "lucide-react";
import api from "@/lib/api";
import { PyramidCard } from "@/components/PyramidCard";
import { PyramidCompactCard } from "@/components/PyramidCompactCard";
import { PyramidListItem } from "@/components/PyramidListItem";
import { PHASES, AREAS, AREA_MAP } from "@/lib/phases";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LETTERS = ["All", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""), "#"];

const FKEY = "ae_archive_filters";
const loadFilters = () => {
  try { return JSON.parse(sessionStorage.getItem(FKEY)) || {}; } catch { return {}; }
};

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const areaParam = searchParams.get("area") || "all";
  const saved = loadFilters();
  const didMount = useRef(false);
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [area, setArea] = useState(searchParams.get("area") || saved.area || "all");
  const [phase, setPhase] = useState(saved.phase || "all");
  const [country, setCountry] = useState(saved.country || "all");
  const [letter, setLetter] = useState(saved.letter || "All");
  const [q, setQ] = useState(saved.q || "");
  const [deepOnly, setDeepOnly] = useState(saved.deepOnly || false);
  const [showAll, setShowAll] = useState(searchParams.get("full") === "1" || saved.showAll || false);
  const [view, setView] = useState(saved.view || "grid");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Respond to later URL ?area= changes, but never clobber restored state on first mount
  useEffect(() => {
    if (!didMount.current) return;
    setArea(areaParam);
    setPhase("all");
  }, [areaParam]);

  // Persist filters so they survive navigating into an entry and back
  useEffect(() => {
    sessionStorage.setItem(FKEY, JSON.stringify({ area, phase, country, letter, q, deepOnly, showAll, view }));
  }, [area, phase, country, letter, q, deepOnly, showAll, view]);

  const setAreaAndUrl = (a) => {
    setArea(a);
    setPhase("all");
    if (a === "all") setSearchParams({}); else setSearchParams({ area: a });
  };

  const structurePhases = PHASES.filter((p) => p.area === "structures");
  const showPhaseChips = area === "structures";

  useEffect(() => {
    const params = {};
    if (area !== "all") params.area = area;
    if (phase !== "all") params.phase = phase;
    if (showAll) params.stubs = "show";
    api.get("/countries", { params }).then((r) => setCountries(r.data)).catch(() => {});
    if (didMount.current) setCountry("all");
  }, [area, phase, showAll]);

  useEffect(() => { didMount.current = true; }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (area !== "all") params.area = area;
    if (phase !== "all") params.phase = phase;
    if (country !== "all") params.country = country;
    if (letter !== "All") params.letter = letter;
    if (q) params.q = q;
    if (showAll) params.stubs = "show";
    const t = setTimeout(() => {
      api.get("/pyramids", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [area, phase, country, letter, q, showAll]);

  useEffect(() => { setPage(0); }, [area, phase, country, letter, q, deepOnly, showAll, view]);

  const shown = deepOnly ? items.filter((p) => !!p.research) : items;
  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const paged = shown.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const phaseChip = (val, label, color) => (
    <button
      key={val}
      data-testid={`phase-filter-${val}`}
      onClick={() => setPhase(val)}
      className="px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors"
      style={{
        borderColor: phase === val ? (color || "hsl(var(--foreground))") : "hsl(var(--border))",
        background: phase === val ? (color || "hsl(var(--foreground))") : "transparent",
        color: phase === val ? "#fff" : "hsl(var(--muted-foreground))",
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-8">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-1.5">
        The Archive · {area === "all" ? "All Areas" : AREA_MAP[area]?.label}
      </p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">Archive Index</h1>

      {/* Row 1: Search + Country */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="search-input-pyramids"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search entries, regions, countries…"
            className="w-full bg-card border border-border pl-10 pr-4 py-2.5 font-mono text-sm focus:outline-none focus:border-accent transition-colors"
          />
        </div>
        <div className="w-full sm:w-56 sm:ml-auto">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="archive-country-select" className="font-mono text-xs uppercase tracking-wider">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Countries</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 2: Area chips + phase sub-chips + toggles */}
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="area-filters">
        <button
          data-testid="area-filter-all"
          onClick={() => setAreaAndUrl("all")}
          className="px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors"
          style={{
            borderColor: area === "all" ? "hsl(var(--foreground))" : "hsl(var(--border))",
            background: area === "all" ? "hsl(var(--foreground))" : "transparent",
            color: area === "all" ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
          }}
        >
          All Areas
        </button>
        {AREAS.map((a) => (
          <button
            key={a.key}
            data-testid={`area-filter-${a.key}`}
            onClick={() => setAreaAndUrl(a.key)}
            className="px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors"
            style={{
              borderColor: area === a.key ? a.color : "hsl(var(--border))",
              background: area === a.key ? a.color : "transparent",
              color: area === a.key ? "#fff" : "hsl(var(--muted-foreground))",
            }}
          >
            {a.label}
          </button>
        ))}
        <span className="mx-1 h-6 w-px bg-border" />
        {showPhaseChips && phaseChip("all", "All Structures")}
        {showPhaseChips && structurePhases.map((ph) => phaseChip(ph.key, `${ph.roman} · ${ph.label}`, ph.color))}
        <button
          data-testid="filter-deep-dives"
          onClick={() => setDeepOnly((v) => !v)}
          className={`flex items-center gap-1.5 px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors ${
            deepOnly ? "bg-accent text-accent-foreground border-accent" : "bg-secondary/60 text-muted-foreground border-border hover:text-foreground"
          }`}
        >
          <Shovel className="h-3 w-3" /> Deep Dives
        </button>
        <button
          data-testid="filter-show-all"
          onClick={() => setShowAll((v) => !v)}
          title="Include thousands of auto-catalogued stub entries (thin records, no deep-dive yet)"
          className={`flex items-center gap-1.5 px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors ${
            showAll ? "bg-foreground text-background border-foreground" : "bg-secondary/60 text-muted-foreground border-border hover:text-foreground"
          }`}
        >
          <Layers className="h-3 w-3" /> {showAll ? "Full Catalogue" : "Curated Only"}
        </button>
      </div>

      {/* Row 3: A-Z (left) + count & view toggle (right) */}
      <div className="mt-3 flex items-center gap-3 border-b border-border pb-2 flex-wrap">
        <div className="flex flex-wrap items-center gap-1 flex-1" data-testid="az-filter">
          {LETTERS.map((L) => (
            <button
              key={L}
              data-testid={`az-${L === "All" ? "all" : L === "#" ? "hash" : L}`}
              onClick={() => setLetter(L)}
              className={`min-w-[26px] h-7 px-1.5 font-mono text-[11px] uppercase tracking-wider border transition-colors ${
                letter === L
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-transparent hover:border-border hover:text-foreground"
              }`}
            >
              {L}
            </button>
          ))}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground shrink-0">
          {loading ? "Loading…" : shown.length === 0 ? "0 entries" :
            `${safePage * PAGE_SIZE + 1}–${Math.min((safePage + 1) * PAGE_SIZE, shown.length)} of ${shown.length}${items.length >= 250 ? "+" : ""}`}
        </p>
        <div className="flex items-center border border-border shrink-0">
          <button
            data-testid="view-grid"
            onClick={() => setView("grid")}
            aria-label="Grid view"
            className={`p-1.5 transition-colors ${view === "grid" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            data-testid="view-compact"
            onClick={() => setView("compact")}
            aria-label="Compact view"
            className={`p-1.5 transition-colors border-l border-border ${view === "compact" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Grid2x2 className="h-4 w-4" />
          </button>
          <button
            data-testid="view-list"
            onClick={() => setView("list")}
            aria-label="List view"
            className={`p-1.5 transition-colors border-l border-border ${view === "list" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
          >
            <ListIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="mt-4">
        {loading ? (
          <p className="font-mono text-sm text-muted-foreground">Loading archive…</p>
        ) : shown.length === 0 ? (
          <p data-testid="no-results" className="font-mono text-sm text-muted-foreground">No entries match your filter.</p>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="archive-grid">
            {paged.map((p, i) => (
              <PyramidCard key={p.slug} p={p} index={i} />
            ))}
          </div>
        ) : view === "compact" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="archive-compact">
            {paged.map((p, i) => (
              <PyramidCompactCard key={p.slug} p={p} index={i} />
            ))}
          </div>
        ) : (
          <div className="border border-border divide-y divide-border" data-testid="archive-list">
            <div className="hidden md:flex items-center gap-5 px-3 py-2 bg-secondary/50 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="h-10 w-14" />
              <span className="flex-1">Site</span>
              <span className="w-32">Phase</span>
              <span className="w-28">Country</span>
              <span className="w-16 text-right">Height</span>
              <span className="w-28 text-right">Age</span>
              <span className="w-4" />
            </div>
            {paged.map((p) => (
              <PyramidListItem key={p.slug} p={p} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && pageCount > 1 && (
          <div className="mt-6 flex items-center justify-center gap-1.5" data-testid="pagination">
            <button
              data-testid="page-prev"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="px-3 h-9 font-mono text-[11px] uppercase tracking-widest border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              ← Prev
            </button>
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                data-testid={`page-${i + 1}`}
                onClick={() => setPage(i)}
                className={`min-w-[36px] h-9 px-2 font-mono text-[11px] border transition-colors ${
                  i === safePage
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              data-testid="page-next"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              className="px-3 h-9 font-mono text-[11px] uppercase tracking-widest border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
